function debounce(fn, wait) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn.apply(this, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');

  if (summaryElement) {
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
  }
}

function parseFacetNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = parseFloat(String(value).replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.bindFormEvents();

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGrid = document.getElementById('ProductGridContainer');

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    if (productGrid?.querySelector('.collection')) {
      productGrid.querySelector('.collection').classList.add('loading');
    }

    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}${searchParams ? `&${searchParams}` : ''}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        FacetFiltersForm.renderFilters(responseText, event);
        FacetFiltersForm.renderProductGridContainer(responseText);
        FacetFiltersForm.renderProductCount(responseText);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(responseText);
        }
      })
      .catch((error) => {
        console.error('Facet fetch error:', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl)?.html;
    if (!html) return;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html);
    }
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');

    if (!source || !target) return;

    target.innerHTML = source.innerHTML;

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    if (window.PXUTheme && window.PXUTheme.infiniteScroll && typeof window.PXUTheme.infiniteScroll.init === 'function') {
      window.PXUTheme.infiniteScroll.init();
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (sourceCount && container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }

    if (sourceCount && containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];

          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId)?.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`)?.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event?.target?.closest('.js-filter')) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        if (newFacetDetailsElement) {
          const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
            ? '.mobile-facets__close-button'
            : '.facets__summary';

          const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
          const isTextInput =
            event.target.getAttribute('type') === 'text' || event.target.getAttribute('type') === 'number';

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }

    document.dispatchEvent(new CustomEvent('facets:updated'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (!activeFacetsElement || !currentElement) return;
      currentElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
  const mobileOpenSource = html.querySelector('.mobile-facets__open');
  const mobileOpenTarget = document.querySelector('.mobile-facets__open');

  if (mobileOpenSource && mobileOpenTarget) {
    mobileOpenTarget.outerHTML = mobileOpenSource.outerHTML;
  }

  ['FacetSortForm', 'FacetSortDrawerForm'].forEach((formId) => {
    const sourceForm = html.getElementById(formId);
    const targetForm = document.getElementById(formId);

    if (sourceForm && targetForm) {
      targetForm.innerHTML = sourceForm.innerHTML;
    }
  });

  document.querySelectorAll('facet-filters-form').forEach((element) => {
    if (typeof element.bindFormEvents === 'function') {
      element.bindFormEvents();
    }
  });

  const menuDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
  if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
    menuDrawer.bindEvents();
  }

  document.dispatchEvent(new CustomEvent('facets:updated'));
}

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!source || !target) return;

    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    history.pushState({ searchParams }, '', url);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];
    return [{ section: productGrid.dataset.id }];
  }

  static createSearchParamsFromForms(forms) {
    const params = new URLSearchParams();
    const seen = new Set();

    forms.filter(Boolean).forEach((form) => {
      const formData = new FormData(form);

      formData.forEach((value, key) => {
        if (value === '' || value === null || value === undefined) return;

        const token = `${key}::${value}`;
        if (seen.has(token)) return;

        seen.add(token);
        params.append(key, value);
      });
    });

    return params.toString();
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event, true);
  }

  onSubmitHandler(event) {
  event.preventDefault();

  const currentForm = event.target.closest('form');
  if (!currentForm) return;

  let searchParams = '';

  switch (currentForm.id) {
    case 'FacetFiltersForm': {
      const filterForm = document.getElementById('FacetFiltersForm');
      const sortSelect = document.querySelector('#FacetSortForm select[name="sort_by"]');

      const params = new URLSearchParams(new FormData(filterForm));

      if (sortSelect && sortSelect.value) {
        params.set('sort_by', sortSelect.value);
      }

      searchParams = params.toString();
      break;
    }

    case 'FacetFiltersFormMobile': {
      const filterForm = document.getElementById('FacetFiltersFormMobile');
      const sortSelect = document.querySelector('#FacetSortDrawerForm select[name="sort_by"]');

      const params = new URLSearchParams(new FormData(filterForm));

      if (sortSelect && sortSelect.value) {
        params.set('sort_by', sortSelect.value);
      }

      searchParams = params.toString();
      break;
    }

    case 'FacetSortForm': {
      const filterForm = document.getElementById('FacetFiltersForm');
      const sortForm = document.getElementById('FacetSortForm');
      const sortSelect = sortForm?.querySelector('select[name="sort_by"]');

      const params = new URLSearchParams(new FormData(filterForm));

      if (sortSelect && sortSelect.value) {
        params.set('sort_by', sortSelect.value);
      }

      searchParams = params.toString();
      break;
    }

    case 'FacetSortDrawerForm': {
      const filterForm = document.getElementById('FacetFiltersFormMobile');
      const sortForm = document.getElementById('FacetSortDrawerForm');
      const sortSelect = sortForm?.querySelector('select[name="sort_by"]');

      const params = new URLSearchParams(new FormData(filterForm));

      if (sortSelect && sortSelect.value) {
        params.set('sort_by', sortSelect.value);
      }

      searchParams = params.toString();
      break;
    }

    default: {
      searchParams = new URLSearchParams(new FormData(currentForm)).toString();
      break;
    }
  }

  this.onSubmitForm(searchParams, event);
}

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const href = event.currentTarget?.href || event.target?.closest('a')?.href || '';
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url, null, true);
  }

  bindFormEvents() {
    const facetForm = this.querySelector('form');
    if (!facetForm) return;

    if (this._boundForm === facetForm) return;

    this._boundForm = facetForm;

    const isSortForm = facetForm.id === 'FacetSortForm' || facetForm.id === 'FacetSortDrawerForm';

    if (isSortForm) {
      // Mobile: sort form lives inside .mobile-facets-drawer — defer apply until "Filter anwenden" click
      if (facetForm.closest('.mobile-facets-drawer')) return;

      // Sort dropdown — no 500ms debounce, turant fire
      const handler = (e) => this.onSubmitHandler(e);
      facetForm.addEventListener('input', handler);
      facetForm.addEventListener('change', handler);
    } else {
      // Filters — 500ms debounce (same as before)
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minInput = this.querySelector('.price-input--min');
    this.maxInput = this.querySelector('.price-input--max');
    this.minRange = this.querySelector('.price-range-slider--min');
    this.maxRange = this.querySelector('.price-range-slider--max');

    if (!this.minInput || !this.maxInput) return;

    this.onNumberInput = this.onNumberInput.bind(this);
    this.onRangeInput = this.onRangeInput.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    [this.minInput, this.maxInput].forEach((element) => {
      element.addEventListener('input', this.onNumberInput);
      element.addEventListener('change', this.onNumberInput);
      element.addEventListener('keydown', this.onKeyDown);
    });

    [this.minRange, this.maxRange].filter(Boolean).forEach((element) => {
      element.addEventListener('input', this.onRangeInput);
      element.addEventListener('change', this.onRangeInput);
    });

    this.syncFromInputs();
    this.updateRangeTrack();
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;

    const pattern = /[0-9]|\.|,|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  getLimits() {
    const hardMin = 0;
    const hardMax =
      parseFacetNumber(this.maxInput.getAttribute('max')) ||
      parseFacetNumber(this.minInput.getAttribute('max')) ||
      parseFacetNumber(this.maxRange?.getAttribute('max')) ||
      0;

    return { hardMin, hardMax };
  }

  clampValues(minValue, maxValue) {
    const { hardMin, hardMax } = this.getLimits();

    let min = Math.max(hardMin, minValue);
    let max = Math.min(hardMax, maxValue);

    if (min > max) {
      [min, max] = [max, min];
    }

    return { min, max };
  }

  syncFromInputs() {
    const minValue = parseFacetNumber(this.minInput.value);
    const maxValue = parseFacetNumber(this.maxInput.value || this.maxInput.getAttribute('max'));
    const { min, max } = this.clampValues(minValue, maxValue);

    this.minInput.value = min;
    this.maxInput.value = max;

    if (this.minRange) this.minRange.value = min;
    if (this.maxRange) this.maxRange.value = max;

    this.updateRangeTrack();
  }

  syncFromRanges() {
    if (!this.minRange || !this.maxRange) return;

    const minValue = parseFacetNumber(this.minRange.value);
    const maxValue = parseFacetNumber(this.maxRange.value);
    const { min, max } = this.clampValues(minValue, maxValue);

    this.minRange.value = min;
    this.maxRange.value = max;
    this.minInput.value = min;
    this.maxInput.value = max;
  }

  updateRangeTrack() {
    if (!this.minRange || !this.maxRange) return;

    const min = parseFacetNumber(this.minRange.min || 0);
    const max = parseFacetNumber(this.minRange.max || 0);
    const minVal = parseFacetNumber(this.minRange.value);
    const maxVal = parseFacetNumber(this.maxRange.value);

    if (max <= min) return;

    const leftPercent = ((minVal - min) / (max - min)) * 100;
    const rightPercent = ((maxVal - min) / (max - min)) * 100;

    const sliderWrap = this.querySelector('.facets__price-range-slider');
    if (!sliderWrap) return;

    sliderWrap.style.setProperty('--range-left', `${leftPercent}%`);
    sliderWrap.style.setProperty('--range-right', `${rightPercent}%`);
  }

  onNumberInput() {
    this.syncFromInputs();
  }

  onRangeInput() {
    this.syncFromRanges();
    this.updateRangeTrack();
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink = this.querySelector('a');
    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!form) return;

    form.onActiveFilterClick(event);
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}

function getFacetGroup(element) {
  return element.closest('.js-filter') || element.closest('.mobile-facets__details');
}

function updateFacetMoreState(scope = document) {
  const filterGroups = scope.querySelectorAll('.js-filter, .mobile-facets__details');

  filterGroups.forEach((group) => {
    const button = group.querySelector('.facets-more-toggle');
    const hiddenItems = group.querySelectorAll('.facet-item--hidden');

    if (!button || !hiddenItems.length) return;

    const hasCheckedHiddenItem = Array.from(hiddenItems).some((item) => {
      const input = item.querySelector('input');
      return input && input.checked;
    });

    if (hasCheckedHiddenItem) {
      group.classList.add('facets-expanded');
      button.setAttribute('aria-expanded', 'true');
      button.textContent = '- less';
    } else {
      group.classList.remove('facets-expanded');
      button.setAttribute('aria-expanded', 'false');
      button.textContent = '+ Mehr';
    }
  });
}

function getMobileDrawer() {
  return document.querySelector('.mobile-facets-drawer');
}

function openMobileDrawer() {
  const drawer = getMobileDrawer();
  if (!drawer) return;

  drawer.classList.add('is-open');
  document.documentElement.style.overflow = 'hidden';
}

function closeMobileDrawer() {
  const drawer = getMobileDrawer();
  if (!drawer) return;

  drawer.classList.remove('is-open');
  document.documentElement.style.overflow = '';
}

if (!window.__customFacetUiBound) {
  window.__customFacetUiBound = true;

  document.addEventListener('click', function (event) {
    const openBtn = event.target.closest('.js-mobile-facets-open');
    if (openBtn) {
      event.preventDefault();
      openMobileDrawer();
      return;
    }

    const closeBtn = event.target.closest('.js-mobile-facets-close');
    if (closeBtn) {
      event.preventDefault();
      closeMobileDrawer();
      return;
    }

    const clearBtn = event.target.closest('.active-facets__button-clear, .clear-btn');
    if (clearBtn) {
      event.preventDefault();
      FacetFiltersForm.renderPage('', null, true);
      closeMobileDrawer();
      return;
    }

    const moreBtn = event.target.closest('.facets-more-toggle');
    if (moreBtn) {
      event.preventDefault();

      const group = getFacetGroup(moreBtn);
      if (!group) return;

      const expanded = group.classList.contains('facets-expanded');

      if (expanded) {
        group.classList.remove('facets-expanded');
        moreBtn.setAttribute('aria-expanded', 'false');
        moreBtn.textContent = '+ Mehr';
      } else {
        group.classList.add('facets-expanded');
        moreBtn.setAttribute('aria-expanded', 'true');
        moreBtn.textContent = '- less';
      }
      return;
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeMobileDrawer();
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    updateFacetMoreState();
  });

  document.addEventListener('facets:updated', function () {
    updateFacetMoreState();
  });
}





///

/**
 * Collection Variant Sync v2
 */

(function () {
  'use strict';

  var COLOR_TAXONOMY_TO_OPTION = {
    '1':    ['Schwarz', 'Black'],
    '2':    ['Blau', 'Blue'],
    '3':    ['Weiß', 'Weiss', 'White', 'Frost White'],
    '4':    ['Gold'],
    '5':    ['Silber', 'Silver'],
    '6':    ['Beige', 'Sand'],
    '7':    ['Braun', 'Brown'],
    '8':    ['Grau', 'Grey', 'Gray'],
    '9':    ['Grün', 'Gruen', 'Army', 'Green'],
    '10':   ['Orange'],
    '11':   ['Pink', 'Rosa'],
    '12':   ['Lila', 'Purple'],
    '13':   ['Rot', 'Red'],
    '14':   ['Gelb', 'Yellow'],
    '15':   ['Marineblau', 'Navy'],
    '16':   ['Türkis', 'Turkis', 'Turquoise'],
    '17':   ['Transparent', 'Clear'],
    '657':  ['Bronze'],
    '2865': ['Mehrfarbig', 'Multicolor']
  };

  var COLOR_TAXONOMY_TO_SWATCH = {
    '1':    ['schwarz'],
    '2':    ['blau'],
    '3':    ['weiss', 'weiß', 'frost_white', 'white'],
    '4':    ['gold'],
    '5':    ['silber', 'silver'],
    '6':    ['beige', 'sand'],
    '7':    ['braun', 'brown'],
    '8':    ['grau', 'grey', 'gray'],
    '9':    ['army', 'gruen', 'grün', 'green'],
    '10':   ['orange'],
    '11':   ['pink', 'rosa'],
    '12':   ['lila', 'purple'],
    '13':   ['rot', 'red'],
    '14':   ['gelb', 'yellow'],
    '15':   ['marineblau', 'navy'],
    '16':   ['türkis', 'turkis', 'turquoise'],
    '17':   ['transparent', 'clear'],
    '657':  ['bronze'],
    '2865': ['mehrfarbig', 'multicolor']
  };

  var SEL = {
    card:            '.product__grid-item',
    variantJson:     'script.product-variant-json',
    mainImage:       '.image-element__wrap img',
    productLink:     '.product-image__wrapper a',
    titleLink:       '.product-thumbnail__title',
    priceCurrent:    '.product-thumbnail__price .money .money',
    comparePrice:    '.product-thumbnail__was-price .money .money',
    wasContainer:    '.product-thumbnail__was-price',
    priceContainer:  '.product-thumbnail__price',
    swatchLink:      '.thumbnail-swatch a.swatch',
    gridContainer:   '#ProductGridContainer',
    noscriptImg:     'noscript.noscript'
  };

  function getActiveFilters() {
    var params = new URLSearchParams(window.location.search);
    var filters = { sizes: [], colorIds: [], colorId: null };

    params.forEach(function (value, key) {
      if (key.indexOf('filter.v.option.') === 0 && value) {
        filters.sizes.push(value.trim());
      }
      // Multiple colours can be selected at once (e.g. Beige + Blue). Collect
      // every selected colour, not just the last one.
      if (key === 'filter.v.t.shopify.color-pattern' && value) {
        var m = value.match(/TaxonomyValue\/(\d+)/);
        if (m && filters.colorIds.indexOf(m[1]) === -1) filters.colorIds.push(m[1]);
      }
    });

    // Backwards-compatible single-colour handle (first selected colour) used by
    // the swatch helpers below.
    filters.colorId = filters.colorIds[0] || null;

    return filters;
  }

  // True when `token` appears inside `optionValue` as part of a single colour
  // word — e.g. token "blau" matches "hellblau/silber" (via "hellblau") but does
  // NOT match "silber/rot". Splitting on separators stops a combined colourway
  // from leaking across unrelated colours.
  function colorWordContains(optionValue, token) {
    if (!optionValue || !token) return false;
    var parts = optionValue.split(/[\s\/,+&-]+/);
    for (var i = 0; i < parts.length; i++) {
      if (parts[i] && parts[i].indexOf(token) !== -1) return true;
    }
    return false;
  }

  function findBestVariant(variants, filters) {
    if (!variants || !variants.length) return null;

    var targetSizes  = (filters.sizes && filters.sizes.length)
      ? filters.sizes.map(function (s) { return s.toUpperCase(); })
      : null;
    // OR across every selected colour: combine the option-name tokens of all
    // active colour filters so a variant matching ANY selected colour counts
    // (e.g. Beige + Blue shows a card's beige OR blue variant).
    var colorValues = null;
    if (filters.colorIds && filters.colorIds.length) {
      colorValues = [];
      for (var ci = 0; ci < filters.colorIds.length; ci++) {
        var cvSet = COLOR_TAXONOMY_TO_OPTION[filters.colorIds[ci]];
        if (cvSet) colorValues = colorValues.concat(cvSet);
      }
      // Colour filter(s) active but none map to known option names -> preserve
      // the SSR image instead of guessing a wrong-colour variant.
      if (!colorValues.length) return null;
    }

    var best      = null;
    var bestScore = -1;

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v.available) continue;

      var opts = [
        (v.option1 || '').trim(),
        (v.option2 || '').trim(),
        (v.option3 || '').trim()
      ];

      var score = 0;

      if (targetSizes) {
        var sizeMatched = false;
        for (var s = 0; s < opts.length && !sizeMatched; s++) {
          if (!opts[s]) continue;
          var optUpper = opts[s].toUpperCase();
          for (var ts = 0; ts < targetSizes.length; ts++) {
            if (optUpper === targetSizes[ts]) {
              sizeMatched = true;
              score += 10;
              break;
            }
          }
        }
        if (!sizeMatched) continue;
      }

      if (colorValues) {
        var exactColor = false;
        var partialColor = false;

        for (var c = 0; c < colorValues.length; c++) {
          var cv = colorValues[c].toLowerCase();
          for (var o = 0; o < opts.length; o++) {
            var ov = opts[o].toLowerCase();
            if (ov === cv) {
              exactColor = true;
              break;
            }
            // Combined colourways (e.g. "Hellblau/Silber") never equal the base
            // colour token but do contain it as a word-part. Match those too.
            if (colorWordContains(ov, cv)) {
              partialColor = true;
            }
          }
          if (exactColor) break;
        }

        // Pick the FIRST AVAILABLE matching variant in variant order (the
        // product's own primary/default colourway that also satisfies the
        // filter), rather than preferring an exact single colour. An exact match
        // and a combined colourway that includes the selected colour score the
        // same, so the earliest matching variant wins the `score > bestScore`
        // tie. Skip only when the variant has no relation to the selected colour
        // at all — that preserves the SSR image instead of swapping to a wrong
        // colour.
        if (exactColor || partialColor) {
          score += 10;
        } else {
          continue;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    }

    return best;
  }

  function makeSizer(src) {
    var base = src.replace(/_\d+x(\.\w+)/, '$1');
    return function (w) {
      return base.replace(/(\.\w+)(\?|$)/, '_' + w + 'x$1$2');
    };
  }

  var WIDTHS = [200,400,600,800,1000,1200,1400,1600,1800,2000,2500,3000,3500,4000,4500,5000];

  function buildSrcset(sizer) {
    return WIDTHS.map(function (w) {
      return sizer(w) + ' ' + w + 'w';
    }).join(',\n    ');
  }

  function updateImage(card, variant) {
    if (!variant.featured_image) return;

    var img = card.querySelector(SEL.mainImage);
    if (!img) return;

    var sizer  = makeSizer(variant.featured_image.src);
    var srcset = buildSrcset(sizer);
    var src    = sizer(1600);

    img.setAttribute('src',         src);
    img.setAttribute('srcset',      srcset);
    img.setAttribute('data-src',    src);
    img.setAttribute('data-srcset', srcset);

    if (variant.featured_image.alt) {
      img.setAttribute('alt', variant.featured_image.alt);
    }

    var noscript = card.querySelector(SEL.noscriptImg);
    if (noscript) {
      var alt = variant.featured_image.alt || '';
      noscript.innerHTML = '<img class="" src="' + sizer(2000) + '" alt="' + alt + '">';
    }
  }

  function updateLinks(card, variant) {
    // Card links point to the PLAIN product URL (no ?variant=) so the product
    // page applies its own "first available colour" default (see
    // sections/product__main.liquid). The card still shows the filter-matched
    // colour's image/price/swatch, but clicking must not pre-select that colour
    // on the PDP — otherwise e.g. the "Rot" filter opened SILBER/ROT instead of
    // the first available SILBER/SCHWARZ. `variant` is kept for call compat.
    var productUrl = card.getAttribute('data-product-url');

    var imageLinks = card.querySelectorAll(SEL.productLink);
    imageLinks.forEach(function (link) {
      var base = productUrl || link.getAttribute('href').split('?')[0];
      link.setAttribute('href', base);
    });

    var titleLink = card.querySelector(SEL.titleLink);
    if (titleLink) {
      var base = productUrl || titleLink.getAttribute('href').split('?')[0];
      titleLink.setAttribute('href', base);
    }
  }

  function updatePrice(card, variant) {
    function formatEUR(cents) {
      if (cents == null) return '';
      var amount = (cents / 100).toFixed(2).replace('.', ',');
      return '€' + amount + ' EUR';
    }

    var priceEl = card.querySelector(SEL.priceCurrent);
    if (priceEl && variant.price != null) {
      priceEl.textContent = formatEUR(variant.price);
    }

    var compareEl    = card.querySelector(SEL.comparePrice);
    var wasContainer = card.querySelector(SEL.wasContainer);
    var priceWrap    = card.querySelector(SEL.priceContainer);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl)    compareEl.textContent = formatEUR(variant.compare_at_price);
      if (wasContainer) wasContainer.style.display = '';
      if (priceWrap)    priceWrap.classList.add('sale');
    } else {
      if (wasContainer) wasContainer.style.display = 'none';
    }
  }

  // Which colour option value on the matched variant satisfied the active colour
  // filter (e.g. "Rot", or the combined colourway "Weiß/Grün/Rot"). This is the
  // ONE colour whose swatch should light up — the swatch of the exact variant
  // whose image/price/link the card is already showing.
  function matchedColorOptionValue(matched, filters) {
    if (!matched) return null;
    var ids = (filters.colorIds && filters.colorIds.length)
      ? filters.colorIds
      : (filters.colorId ? [filters.colorId] : []);

    var colorValues = [];
    for (var i = 0; i < ids.length; i++) {
      var set = COLOR_TAXONOMY_TO_OPTION[ids[i]];
      if (set) colorValues = colorValues.concat(set);
    }
    if (!colorValues.length) return null;

    var opts = [
      (matched.option1 || '').trim(),
      (matched.option2 || '').trim(),
      (matched.option3 || '').trim()
    ];

    // Prefer an exact single-colour option value (mirrors findBestVariant).
    for (var c = 0; c < colorValues.length; c++) {
      var cv = colorValues[c].toLowerCase();
      for (var o = 0; o < opts.length; o++) {
        if (opts[o] && opts[o].toLowerCase() === cv) return opts[o];
      }
    }
    // Otherwise the combined colourway that contains the selected colour.
    for (var o2 = 0; o2 < opts.length; o2++) {
      if (!opts[o2]) continue;
      var ov = opts[o2].toLowerCase();
      for (var c2 = 0; c2 < colorValues.length; c2++) {
        if (colorWordContains(ov, colorValues[c2].toLowerCase())) return opts[o2];
      }
    }
    return null;
  }

  // Mirror the Liquid that builds data-swatch-name:
  //   data-swatch-name="meta-<option>_<value | replace: ' ', '_' | downcase>"
  function swatchValueToken(value) {
    return (value || '').replace(/ /g, '_').toLowerCase();
  }

  // The value segment of a data-swatch-name ("meta-<option>_<value>"). The option
  // name (the swatch trigger, e.g. "farbe") never contains '_', whereas a value's
  // internal spaces are converted to '_', so the FIRST '_' always separates the
  // option from the value. Comparing this whole segment for equality avoids the
  // suffix collision where "Blue" ("_blue") would otherwise match "Navy Blue"
  // ("…_navy_blue").
  function swatchNameValue(swName) {
    var i = swName.indexOf('_');
    return i === -1 ? '' : swName.slice(i + 1);
  }

  // Like colorWordContains, but requires a WHOLE separated word-part to EQUAL the
  // token. So "Rot" matches the "…/rot" part of "silber/grün/rot", but the filter
  // "Blau" never matches the standalone colour "marineblau" (Navy) just because
  // "blau" is a substring of it. Used to gate which swatch belongs to a filter.
  function colorPartEquals(value, token) {
    if (!value || !token) return false;
    var parts = value.split(/[\s\/,+&-]+/);
    for (var i = 0; i < parts.length; i++) {
      if (parts[i] && parts[i] === token) return true;
    }
    return false;
  }

  function highlightSwatch(card, matched, filters) {
    var swatches = card.querySelectorAll(SEL.swatchLink);
    if (!swatches.length) return;

    // Preferred: light up ONLY the swatch of the matched (first available)
    // variant — the same variant whose image the card is showing — so the
    // active swatch and the visible image always agree. Match the swatch's whole
    // value segment exactly, so "Rot" lights up "…_rot" but never the combined
    // "…_weiß/grün/rot" (and "Blue" never lights up "Navy Blue"), and vice-versa.
    var colorVal = matchedColorOptionValue(matched, filters);
    if (colorVal) {
      var want = swatchValueToken(colorVal);
      for (var i = 0; i < swatches.length; i++) {
        var swName = (swatches[i].getAttribute('data-swatch-name') || '').toLowerCase();
        if (swatchNameValue(swName) === want) {
          swatches.forEach(function (s) { s.classList.remove('swatch--active'); });
          swatches[i].classList.add('swatch--active');
          return;
        }
      }
    }

    // Fallback (colour value could not be resolved to a specific swatch): keep
    // the original behaviour of lighting up every colour-matching swatch so a
    // filtered card is never left without an active swatch.
    var ids = (filters.colorIds && filters.colorIds.length)
      ? filters.colorIds
      : (filters.colorId ? [filters.colorId] : []);
    if (!ids.length) return;

    var names = [];
    for (var k = 0; k < ids.length; k++) {
      var set = COLOR_TAXONOMY_TO_SWATCH[ids[k]];
      if (set) names = names.concat(set);
    }
    if (!names.length) return;

    swatches.forEach(function (s) { s.classList.remove('swatch--active'); });

    swatches.forEach(function (sw) {
      var swName = (sw.getAttribute('data-swatch-name') || '').toLowerCase();
      for (var n = 0; n < names.length; n++) {
        if (swName.indexOf(names[n]) !== -1) {
          sw.classList.add('swatch--active');
          break;
        }
      }
    });
  }

  function fallbackSwatchSync(card, filters) {
    if (!filters.colorId) return;

    var names = COLOR_TAXONOMY_TO_SWATCH[filters.colorId];
    if (!names) return;

    var swatches = card.querySelectorAll(SEL.swatchLink);
    if (!swatches.length) return;

    for (var i = 0; i < swatches.length; i++) {
      var swName = (swatches[i].getAttribute('data-swatch-name') || '').toLowerCase();
      var matched = false;
      for (var n = 0; n < names.length; n++) {
        if (swName.indexOf(names[n]) !== -1) { matched = true; break; }
      }
      if (!matched) continue;

      var span = swatches[i].querySelector('span[data-image]');
      if (span) {
        var dataImg = span.getAttribute('data-image');
        if (dataImg) {
          var img = card.querySelector(SEL.mainImage);
          if (img) {
            var sizer  = makeSizer(dataImg);
            var srcset = buildSrcset(sizer);
            img.setAttribute('src',         sizer(1600));
            img.setAttribute('srcset',      srcset);
            img.setAttribute('data-src',    sizer(1600));
            img.setAttribute('data-srcset', srcset);
          }
        }
      }

      // NOTE: intentionally do NOT copy the swatch's ?variant= onto the product/
      // title link. The card image above reflects the filter, but the link stays
      // the plain product URL so the PDP applies its own first-available default.

      swatches.forEach(function (s) { s.classList.remove('swatch--active'); });
      swatches[i].classList.add('swatch--active');
      break;
    }
  }

  // True when one of the variant's option values is this swatch's colour value.
  function variantHasColor(v, swVal) {
    return swatchValueToken((v.option1 || '').trim()) === swVal
        || swatchValueToken((v.option2 || '').trim()) === swVal
        || swatchValueToken((v.option3 || '').trim()) === swVal;
  }

  // No filter active: light up the FIRST swatch (in display order) whose colour
  // has any available variant. If the first colour is entirely sold out, fall
  // through to the second, then the third, and so on — strict swatch order.
  // Image/price/link are left exactly as server-rendered.
  function highlightDefaultSwatch(card, variants) {
    // Find the product's first-available variant. The variants array is SIZE-major
    // (option1=Größe, option2=Farbe), so this is the first in-stock colour within
    // the first in-stock SIZE — the SAME variant the product page opens on (e.g.
    // S/M + Silber/Schwarz when the title colourway Weiß/Grün/Rot is sold out in
    // S/M). This makes the card show the correct combination's image, matching its
    // PDP, regardless of any active filter.
    var firstAvail = null;
    var firstIdx = -1;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available) { firstAvail = variants[i]; firstIdx = i; break; }
    }
    if (!firstAvail) return;

    // Swap the card image to that variant. Needed only when the naturally-first
    // variant is sold out (firstIdx > 0) — then first-available is a later variant
    // whose image differs from the server-rendered featured image. When variants[0]
    // is in stock the card already shows the right image, so leave it untouched
    // (self-limiting: no store-wide change). Runs whether or not the card renders
    // colour swatches — these cards have none, and the image is the point.
    //
    // EXCEPTION: cards marked data-title-color-locked already show the image of the
    // colour written in the product title (see snippets/title-color-variant.liquid).
    // That image is tied to the title even when the colourway is sold out (Rule 2),
    // so it must NOT be swapped to the first-available colour here. The swatch
    // highlight below still runs (image-only change; selectors untouched).
    if (firstIdx > 0 && !card.hasAttribute('data-title-color-locked')) {
      updateImage(card, firstAvail);
    }

    // If the card DOES render colour swatches, light up the first-available one.
    var swatches = card.querySelectorAll(SEL.swatchLink);
    if (!swatches.length) return;
    for (var s = 0; s < swatches.length; s++) {
      var swVal = swatchNameValue((swatches[s].getAttribute('data-swatch-name') || '').toLowerCase());
      if (!swVal) continue;
      if (variantHasColor(firstAvail, swVal)) {
        swatches.forEach(function (x) { x.classList.remove('swatch--active'); });
        swatches[s].classList.add('swatch--active');
        return;
      }
    }
  }

  // Filter active: walk the swatches in DISPLAY order and return the FIRST one
  // that satisfies the active filters AND has an available variant (together with
  // that variant, for image/price/link). If the first matching colour is sold out
  // for the chosen size, move to the next colour, and so on. Returns null when
  // nothing matches so the caller can fall back / preserve the SSR image.
  function pickFilteredBySwatchOrder(card, variants, filters, swatches) {
    var ids = (filters.colorIds && filters.colorIds.length)
      ? filters.colorIds
      : (filters.colorId ? [filters.colorId] : []);

    var colorValues = [];
    for (var k = 0; k < ids.length; k++) {
      var set = COLOR_TAXONOMY_TO_OPTION[ids[k]];
      if (set) colorValues = colorValues.concat(set);
    }
    // Colour filtered but no known option mapping -> bail (keep SSR image).
    if (ids.length && !colorValues.length) return null;

    var targetSizes = (filters.sizes && filters.sizes.length)
      ? filters.sizes.map(function (x) { return x.toUpperCase(); })
      : null;

    for (var s = 0; s < swatches.length; s++) {
      var swVal = swatchNameValue((swatches[s].getAttribute('data-swatch-name') || '').toLowerCase());
      if (!swVal) continue;

      // Colour filter: the swatch's colour must include a selected colour.
      if (colorValues.length) {
        var colorOk = false;
        for (var c = 0; c < colorValues.length; c++) {
          var cv = colorValues[c].toLowerCase();
          if (swVal === cv || colorPartEquals(swVal, cv)) { colorOk = true; break; }
        }
        if (!colorOk) continue;
      }

      // First available variant of this colour that also satisfies the size filter.
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.available || !variantHasColor(v, swVal)) continue;

        if (targetSizes) {
          var opts = [
            (v.option1 || '').trim().toUpperCase(),
            (v.option2 || '').trim().toUpperCase(),
            (v.option3 || '').trim().toUpperCase()
          ];
          var sizeOk = false;
          for (var o = 0; o < opts.length && !sizeOk; o++) {
            if (!opts[o]) continue;
            for (var t = 0; t < targetSizes.length; t++) {
              if (opts[o] === targetSizes[t]) { sizeOk = true; break; }
            }
          }
          if (!sizeOk) continue;
        }

        return { swatch: swatches[s], variant: v };
      }
    }
    return null;
  }

function syncAllCards() {
    // Every collection card ALWAYS represents the product's first-available variant
    // (image + swatch) — the SAME variant its product page opens on — regardless of
    // any active filter. Filters only decide WHICH products are shown; they no
    // longer swap a card to the filter-matched colour, so a card and its PDP can
    // never disagree (e.g. under the "Rot" filter the Weiß/Grün/Rot product's card
    // shows its first-available Silber/Schwarz, exactly like its product page).
    //
    // pickFilteredBySwatchOrder / findBestVariant / highlightSwatch /
    // fallbackSwatchSync / updateLinks / updatePrice are intentionally no longer
    // called from here (kept defined for an easy revert if the colour-matched
    // preview is ever wanted back).
    var cards = document.querySelectorAll(SEL.card);

    cards.forEach(function (card) {
      var jsonEl = card.querySelector(SEL.variantJson);
      if (!jsonEl) return;

      var variants;
      try {
        variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        return;
      }

      highlightDefaultSwatch(card, variants);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllCards);
  } else {
    syncAllCards();
  }

  var grid = document.querySelector(SEL.gridContainer);
  if (grid) {
    var debounceTimer;
    new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(syncAllCards, 200);
    }).observe(grid, { childList: true, subtree: true });
  }

})();